import * as lamejs from '@breezystack/lamejs';
/**
 * Helper class for common tasks associated with converting
 * and encoding mp3 data with lamejs
 */
export class Mp3Helper {
    constructor(encoderOptions) {
        this.stereoNumChannels = 2;
        /**
         * Generate a Blob of mp3 data from an array of Uint8Arrays.
         * @param bufferCollection The array of Uint8Arrays to generate the mp3 blob from
         */
        this.generateMp3Blob = (bufferCollection) => new Blob(bufferCollection, { type: 'audio/mpeg' });
        /**
         * Generate a downloadable mp3 url from an array of mp3 encoded Uint8Arrays.
         * @param bufferCollection The array of Uint8Arrays to generate the mp3 blob from
         */
        this.generateDownloadableUrl = (bufferCollection) => {
            const mp3Blob = this.generateMp3Blob(bufferCollection);
            return window.URL.createObjectURL(mp3Blob);
        };
        /**
         * Call through to lame.js flush method which finalizes the mp3 buffer.
         * Should always be called once after encoding the recording
         */
        this.getFinalMp3Buffer = () => this.mp3Encoder.flush();
        /**
         * Creates an .mp3 file from the given blob.
         * @param blob The recorded audio blob
         * @param name File name
         */
        this.generateMp3FileFromBlob = (blob, name) => {
            return new File([blob], name, { type: 'audio/mpeg' });
        };
        /**
         * Creates an .mp3 file from given buffer.
         * @param bufferCollection The array of Uint8Arrays to generate the mp3 file from
         * @param name File name
         */
        this.generateMp3FileFromBuffer = (bufferCollection, name) => {
            return this.generateMp3FileFromBlob(this.generateMp3Blob(bufferCollection), name);
        };
        /**
         * Trims an audio buffer to specified start and end times, returning an
         * mp3 encoded Uint8Array
         * @param params The TrimAndEncodeOptions required to trim and encode the buffer
         */
        this.trimAndEncodeBuffer = (params) => {
            const newFrameCount = (params.regionEnd - params.regionStart) * this.encoderOptions.sampleRate;
            const startIndex = Math.round(params.regionStart * this.encoderOptions.sampleRate);
            const endIndex = startIndex + newFrameCount;
            const trimmedChannelData = [];
            for (let i = 0; i < params.channelData.length; i++) {
                trimmedChannelData[i] = params.channelData[i].subarray(startIndex, endIndex);
            }
            return this.encodeBuffer(trimmedChannelData);
        };
        /**
         * Creates and returns a Uint8Array of encoded mp3 data from
         * the given input arrays. Handles mono and stereo.
         * @param bufferArrs The source arrays of samples to encode to mp3 data
         */
        this.encodeBuffer = (bufferArrs) => {
            for (let i = 0; i < bufferArrs.length; i++) {
                if (bufferArrs[i].constructor === Float32Array) {
                    bufferArrs[i] = this.convertFloat32ArrToInt16Arr(bufferArrs[i]);
                }
                if (this.hasEmptyChannel(bufferArrs[i].length)) {
                    return new Uint8Array();
                }
            }
            return bufferArrs.length >= this.stereoNumChannels
                ? this.mp3Encoder.encodeBuffer(bufferArrs[0], bufferArrs[1])
                : this.mp3Encoder.encodeBuffer(bufferArrs[0]);
        };
        this.hasEmptyChannel = (channelLength) => {
            return channelLength < 1;
        };
        /**
         * Helper function for creating a new Int16Array from a Float32Array
         * @param inputArr The Float32Array to be converted to an Int16Array
         */
        this.convertFloat32ArrToInt16Arr = (inputArr) => {
            const convertedSamples = new DataView(new ArrayBuffer(inputArr.length * 2));
            for (let i = 0; i < inputArr.length; i++) {
                const multiplier = inputArr[i] < 0 ? 0x8000 : 0x7fff; // 16-bit signed range is -32768 to 32767
                const rawValue = (inputArr[i] * multiplier) | 0;
                const qualityFilteredValue = rawValue < -32767 ? -32767 : rawValue > 32767 ? 32767 : rawValue; // cap values to filter out clicks and pops
                convertedSamples.setInt16(i * 2, qualityFilteredValue, true); // index, value, little endian
            }
            return new Int16Array(convertedSamples.buffer);
        };
        this.encoderOptions = encoderOptions;
        this.mp3Encoder = new lamejs.Mp3Encoder(encoderOptions.channelCount, encoderOptions.sampleRate, encoderOptions.bitRate);
    }
}
